import {
  AdminTableHeader,
  Banner,
  Button,
  CategoryBadge,
  EmptyState,
  FormError,
  Input,
  Label,
  SectionHeading,
  sonnerToast,
  useDocumentTitle,
} from "@simplicity/ui";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { apiFetch } from "../../api";
import type {
  ConfigRevision,
  Connector,
  Destination,
  Project,
  StreamState,
  TransformDefinition,
} from "../../configTypes";
import { useDetailForm } from "../../hooks/useDetailForm";
import { tryParseJson } from "../../utils";
import { TypeBadge } from "../TypeBadge";
import { Breadcrumbs } from "./Breadcrumbs";
import { ConnectorConfigJsonSheet } from "./ConnectorConfigJsonSheet";
import { TableRunStatusContext } from "./TableRunStatusContext";
import {
  TransformCrossRefContext,
  type TransformCrossRefContextValue,
} from "./TransformCrossRefContext";
import { ConnectorAdvancedOptions, ConnectorVarsForm, SecretsForm } from "./connectors";
import { EffectiveScopePanel } from "./connectors/EffectiveScopePanel";
import { useDirtyForm } from "./useDirtyForm";
import { useTableRunStatus, type RunStatusTarget } from "./useTableRunStatus";
import { useUnsavedChangesGuard } from "./useUnsavedChangesGuard";
import { VersionHistoryList } from "./version-history/VersionHistoryList";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function startCase(s: string): string {
  return s.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function fetchRevisions(id: string, setRevisions: (r: ConfigRevision[]) => void) {
  apiFetch<ConfigRevision[]>(`/api/connectors/${id}/config-revisions`)
    .then((r) => setRevisions(r))
    .catch(() => {}); // Revisions are supplementary — silently fail.
}

// ─── Stream States table ────────────────────────────────────────────────────

function StreamStatesTable({ streamStates }: { streamStates: StreamState[] }) {
  if (streamStates.length === 0) {
    return (
      <div className="admin-table-container">
        <div className="admin-table-header">
          <SectionHeading size="lg" as="h2">
            Recent Runs
          </SectionHeading>
        </div>
        <EmptyState title="No stream states" />
      </div>
    );
  }
  return (
    <div className="admin-table-container">
      <div className="admin-table-header">
        <SectionHeading size="lg" as="h2">
          Recent Runs
        </SectionHeading>
      </div>
      <div className="max-h-[220px] overflow-y-auto">
        <StreamStatesRows streamStates={streamStates} />
      </div>
    </div>
  );
}

function StreamStatesRows({ streamStates }: { streamStates: StreamState[] }) {
  return (
    <table className="admin-table">
      <AdminTableHeader>
        <tr>
          <th>Key</th>
          <th>Mode</th>
          <th>Created</th>
          <th>Start State</th>
          <th>Next State</th>
        </tr>
      </AdminTableHeader>
      <tbody>
        {streamStates.map((ss) => (
          <tr key={ss.id}>
            <td>{ss.key}</td>
            <td>
              <CategoryBadge color={ss.mode === "incremental" ? "emerald" : "violet"}>
                {startCase(ss.mode)}
              </CategoryBadge>
            </td>
            <td className="col-muted">{formatDate(ss.createdAt)}</td>
            <td className="cell-state" title={JSON.stringify(ss.startState)}>
              {JSON.stringify(ss.startState)}
            </td>
            <td className="cell-state" title={JSON.stringify(ss.nextState)}>
              {JSON.stringify(ss.nextState)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Form sub-sections ──────────────────────────────────────────────────────

function NamePrefixFields(props: {
  name: string;
  setName: (v: string) => void;
  prefix: string;
  setPrefix: (v: string) => void;
  clearBanner: () => void;
}) {
  return (
    <div className="flex gap-4">
      <div className="mb-4 flex-1">
        <Label htmlFor="conn-name" className="form-label">
          Name
        </Label>
        <Input
          id="conn-name"
          type="text"
          value={props.name}
          onChange={(e) => {
            props.setName(e.target.value);
            props.clearBanner();
          }}
          placeholder="Connector name"
          autoComplete="one-time-code"
        />
      </div>
      <div className="mb-4 flex-1">
        <Label htmlFor="conn-prefix" className="form-label">
          Prefix
        </Label>
        <Input
          id="conn-prefix"
          type="text"
          value={props.prefix}
          onChange={(e) => {
            props.setPrefix(e.target.value);
            props.clearBanner();
          }}
          autoComplete="one-time-code"
        />
      </div>
    </div>
  );
}

function BaseUrlField(props: {
  baseUrl: string;
  setBaseUrl: (v: string | undefined) => void;
  clearBanner: () => void;
}) {
  return (
    <div className="mb-4">
      <Label htmlFor="conn-base-url" className="form-label">
        Base URL
      </Label>
      <Input
        id="conn-base-url"
        type="text"
        value={props.baseUrl}
        onChange={(e) => {
          props.setBaseUrl(e.target.value);
          props.clearBanner();
        }}
        placeholder="https://..."
        className="font-mono"
        autoComplete="one-time-code"
      />
    </div>
  );
}

// ─── State hook ─────────────────────────────────────────────────────────────

type ConnectorLoaded = {
  connector: Connector;
  streamStates: StreamState[];
};

async function loadConnectorBundle(id: string): Promise<ConnectorLoaded> {
  const [connector, streamStates] = await Promise.all([
    apiFetch<Connector>(`/api/connectors/${id}`),
    apiFetch<StreamState[]>(`/api/connectors/${id}/stream-states`),
  ]);
  return { connector, streamStates };
}

type SecretsParse = { ok: true; value: Record<string, string> } | { ok: false; error: string };

/**
 * SIM-3408: the secrets box is free-text JSON, and anything but a string→string
 * object is a request the server must reject. Stop here with a reason instead of
 * substituting `{}` and sending a save that cannot succeed.
 */
function parseSecrets(text: string): SecretsParse {
  if (!text || text.trim() === "") {
    return { ok: false, error: "Secret references are empty — fill them in before saving." };
  }
  const parsed = tryParseJson(text);
  if (!parsed.ok) {
    return { ok: false, error: `Secret references are not valid JSON: ${parsed.error}` };
  }
  const value = parsed.value;
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return { ok: false, error: "Secret references must be a JSON object of key/value pairs." };
  }
  const entries = Object.entries(value as Record<string, unknown>);
  const nonText = entries.filter(([, v]) => typeof v !== "string").map(([k]) => k);
  if (nonText.length > 0) {
    return { ok: false, error: `Secret reference values must be text: ${nonText.join(", ")}.` };
  }
  return { ok: true, value: Object.fromEntries(entries) as Record<string, string> };
}

type ConnectorFormFields = {
  name: string;
  prefix: string;
  vars: Record<string, unknown>;
  secretsText: string;
};

async function saveConnector(
  id: string | undefined,
  fields: ConnectorFormFields,
): Promise<Connector> {
  const secrets = parseSecrets(fields.secretsText);
  if (!secrets.ok) throw new Error(secrets.error);
  // apiFetch's ApiError already carries the server's message, so a 400 names its field.
  return apiFetch<Connector>(`/api/connectors/${id}`, {
    method: "PATCH",
    body: JSON.stringify({
      name: fields.name.trim() || undefined,
      prefix: fields.prefix.trim(),
      vars: fields.vars,
      secrets: secrets.value,
    }),
  });
}

type ConnectorSiblings = {
  setProjectName: (name: string) => void;
  setDestinationType: (type: string | undefined) => void;
  setTransforms: (ts: TransformDefinition[]) => void;
  setRevisions: (r: ConfigRevision[]) => void;
};

async function loadConnectorSiblings(c: Connector, dst: ConnectorSiblings) {
  const proj = await apiFetch<Project>(`/api/projects/${c.projectId}`);
  dst.setProjectName(proj.name);
  const dests = await apiFetch<Destination[]>(`/api/projects/${c.projectId}/destinations`);
  if (dests.length > 0) dst.setDestinationType(dests[0].type);
  const ts = await apiFetch<TransformDefinition[]>(
    `/api/projects/${c.projectId}/transform-definitions`,
  );
  dst.setTransforms(ts);
  fetchRevisions(c.id, dst.setRevisions);
}

function useConnectorDetail(id: string | undefined) {
  const [connector, setConnector] = useState<Connector | null>(null);
  const [projectName, setProjectName] = useState("");
  const [destinationType, setDestinationType] = useState<string | undefined>();
  const [streamStates, setStreamStates] = useState<StreamState[]>([]);
  const [transforms, setTransforms] = useState<TransformDefinition[]>([]);
  const [revisions, setRevisions] = useState<ConfigRevision[]>([]);
  const [name, setName] = useState("");
  const [prefix, setPrefix] = useState("");
  const [vars, setVars] = useState<Record<string, unknown>>({});
  const [secretsText, setSecretsText] = useState("");
  const [secretsError, setSecretsError] = useState<string | null>(null);

  const { isDirty, setBaseline, resetBaseline } = useDirtyForm({
    name,
    prefix,
    varsJson: JSON.stringify(vars),
    secretsText,
  });

  const form = useDetailForm<ConnectorLoaded, Connector>({
    id,
    fetcher: loadConnectorBundle,
    onLoaded: async ({ connector: c, streamStates: ss }) => {
      setConnector(c);
      setName(c.name);
      setPrefix(c.prefix);
      setVars(c.vars);
      setSecretsText(JSON.stringify(c.secrets, null, 2));
      setBaseline({
        name: c.name,
        prefix: c.prefix,
        varsJson: JSON.stringify(c.vars),
        secretsText: JSON.stringify(c.secrets, null, 2),
      });
      setStreamStates(ss);
      await loadConnectorSiblings(c, {
        setProjectName,
        setDestinationType,
        setTransforms,
        setRevisions,
      });
    },
    validate: () => {
      const parsed = parseSecrets(secretsText);
      setSecretsError(parsed.ok ? null : parsed.error);
      return parsed.ok ? null : parsed.error;
    },
    saver: () => saveConnector(id, { name, prefix, vars, secretsText }),
    onSaved: (updated) => {
      setConnector(updated);
      resetBaseline();
      if (id) fetchRevisions(id, setRevisions);
    },
    successMessage: "Updated configuration.",
  });

  return {
    connector,
    setConnector,
    projectName,
    destinationType,
    streamStates,
    transforms,
    revisions,
    loading: form.loading,
    error: form.error,
    name,
    setName,
    prefix,
    setPrefix,
    vars,
    setVars,
    secretsText,
    setSecretsText,
    secretsError,
    setSecretsError,
    saving: form.saving,
    save: form.save,
    notification: form.notification,
    saveLabel: form.saveLabel,
    clearNotification: form.clearNotification,
    isDirty,
  };
}

async function handleArchive(
  id: string | undefined,
  projectId: string | undefined,
  navigate: ReturnType<typeof useNavigate>,
) {
  if (!confirm("Are you sure you want to archive this connector?")) return;
  try {
    await apiFetch(`/api/connectors/${id}/archive`, { method: "POST" });
    sonnerToast.info("Archived Connector");
    void navigate(`/config/projects/${projectId}`);
  } catch (err) {
    sonnerToast.error(err instanceof Error ? err.message : "Archive failed");
  }
}

// ─── Form body (extracted to keep ConnectorDetails under limits) ────────────

function updateBaseUrl(
  vars: Record<string, unknown>,
  setVars: (v: Record<string, unknown>) => void,
  clearBanner: () => void,
  value: string | undefined,
) {
  const next = { ...vars };
  if (value) {
    next.baseUrl = value;
  } else {
    delete next.baseUrl;
  }
  setVars(next);
  clearBanner();
}

function ConnectorFormBody(props: {
  s: ReturnType<typeof useConnectorDetail>;
  id: string | undefined;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const { s, id } = props;
  const clearBanner = s.clearNotification;
  // An invalid schedule time is never written through to `vars`, so submitting would
  // save the previous time and report success. Block Save until it is fixed (SIM-3404).
  const [scheduleIssue, setScheduleIssue] = useState<string | null>(null);
  // ConnectorFormBody is only rendered when s.connector is non-null
  const connector = s.connector as Connector;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (scheduleIssue) return;
        void s.save();
      }}
    >
      <NamePrefixFields
        name={s.name}
        setName={s.setName}
        prefix={s.prefix}
        setPrefix={s.setPrefix}
        clearBanner={clearBanner}
      />
      {"baseUrl" in s.vars && (
        <BaseUrlField
          baseUrl={typeof s.vars.baseUrl === "string" ? s.vars.baseUrl : ""}
          setBaseUrl={(v) => updateBaseUrl(s.vars, s.setVars, clearBanner, v)}
          clearBanner={clearBanner}
        />
      )}
      <div className="mb-4 space-y-4">
        <ConnectorVarsForm
          vars={s.vars}
          onVarsChange={(v) => {
            s.setVars(v);
            clearBanner();
          }}
          connectorType={connector.type}
          connectorId={connector.id}
          connectorPrefix={s.prefix}
          destinationType={s.destinationType}
          clearBanner={clearBanner}
          onScheduleInvalidChange={setScheduleIssue}
          hideAdvanced
        />
      </div>
      <SecretsForm
        connectorType={connector.type}
        secretsText={s.secretsText}
        setSecretsText={s.setSecretsText}
        setSecretsError={s.setSecretsError}
        clearBanner={clearBanner}
      />
      <ConnectorAdvancedOptions
        vars={s.vars}
        onVarsChange={(v) => {
          s.setVars(v);
          clearBanner();
        }}
        connectorType={connector.type}
        connectorPrefix={s.prefix}
        clearBanner={clearBanner}
      />
      {s.secretsError && <FormError assertive>{s.secretsError}</FormError>}
      {scheduleIssue && <FormError assertive>{scheduleIssue}</FormError>}
      <div className="form-actions">
        <Button
          type="button"
          variant="destructiveOutline"
          onClick={() => void handleArchive(id, connector.projectId, props.navigate)}
        >
          Archive
        </Button>
        <Button
          type="submit"
          disabled={
            s.saving || !s.isDirty || !s.name.trim() || !s.prefix.trim() || scheduleIssue !== null
          }
        >
          {s.saving ? "Saving..." : s.saveLabel}
        </Button>
      </div>
    </form>
  );
}

/** SIM-2729: null until the connector loads. Extracted so the call site in
 * `ConnectorDetails` carries no optional chaining (it was at its complexity cap). */
function runStatusTarget(connector: Connector | null): RunStatusTarget | null {
  if (connector == null) return null;
  return { projectId: connector.projectId, connectorId: connector.id };
}

// ─── Main component ─────────────────────────────────────────────────────────

export function ConnectorDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const s = useConnectorDetail(id);
  const { dialog: unsavedChangesDialog } = useUnsavedChangesGuard(s.isDirty);

  useDocumentTitle(s.connector?.name ?? null, { suffix: " | Simplicity Admin" });

  // SIM-2729: keyed on the SAVED prefix, not the editable form field — freshness
  // describes what exists in BQ today, which tracks the persisted prefix. Using
  // the form value would refetch on every keystroke in the Prefix input.
  const runStatus = useTableRunStatus(runStatusTarget(s.connector));

  if (s.loading) return <div className="empty-state">Loading connector...</div>;
  if (s.error || !s.connector) {
    return <div className="empty-state text-destructive">{s.error ?? "Connector not found"}</div>;
  }

  const crossRefValue: TransformCrossRefContextValue = {
    transforms: s.transforms,
    connectorPrefix: s.prefix,
  };

  return (
    <TransformCrossRefContext.Provider value={crossRefValue}>
      <TableRunStatusContext.Provider value={runStatus}>
        <div className="mx-auto max-w-4xl">
          <Breadcrumbs
            items={[
              { label: "Projects", to: "/config/projects" },
              {
                label: s.projectName || "...",
                to: `/config/projects/${s.connector.projectId}#connectors`,
              },
              { label: s.connector.name },
            ]}
          />
          <div className="form-card mb-6">
            <div className="mb-4 flex items-center justify-between">
              <SectionHeading size="lg" as="h2">
                Edit Connector
              </SectionHeading>
              <div className="flex items-center gap-2">
                <ConnectorConfigJsonSheet connector={s.connector} hasUnsavedChanges={s.isDirty} />
                <TypeBadge type={s.connector.type} />
              </div>
            </div>
            {s.notification && (
              <Banner
                variant={s.notification.variant}
                onDismiss={s.clearNotification}
                className="mb-4"
              >
                {s.notification.message}
              </Banner>
            )}
            <div className="mb-4 font-mono text-sm text-muted-foreground">ID: {s.connector.id}</div>
            <ConnectorFormBody s={s} id={id} navigate={navigate} />
          </div>
          {s.connector.type === "powerschools" && (
            <EffectiveScopePanel connectorId={s.connector.id} />
          )}
          <StreamStatesTable streamStates={s.streamStates} />
          <div className="mt-6">
            <VersionHistoryList
              revisions={s.revisions}
              currentVars={s.vars}
              onRestoreVars={(vars) => s.setVars(vars)}
            />
          </div>
          {unsavedChangesDialog}
        </div>
      </TableRunStatusContext.Provider>
    </TransformCrossRefContext.Provider>
  );
}
